import React, { useState, useEffect, useRef } from 'react';
import { User, UserRole, SurveyData, DynamicQuestion, AttendanceRecord, StaffMember } from '../types';
import { 
  getSurveys, getDynamicQuestions, getAttendanceByDate, markAttendance, 
  extractStudentsFromSurvey, clearAttendance, getCategoryForAge, getCategoryName, getMemberAge,
  subscribeAttendanceByDate, getStaff
} from '../services/storage';
import { useToast } from './Toast';
import { 
  Loader, ScanBarcode, CheckCircle, Clock, Calendar, Users, X, Download,
  Search, Filter, FileText, Printer, AlertTriangle, Sparkles
} from 'lucide-react';
import { jsPDF } from 'jspdf';

interface AttendanceManagerProps {
  currentUser: User;
}

const AttendanceManager: React.FC<AttendanceManagerProps> = ({ currentUser }) => {
  const isViewOnly = currentUser.role === UserRole.VIEW_ONLY_ADMIN;
  const { addToast } = useToast();
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<any[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  
  // Search & Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showReport, setShowReport] = useState(false);
  const [reportTab, setReportTab] = useState<'STUDENT' | 'STAFF'>('STUDENT');
  
  // Staff search & filters state
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [staffRoleFilter, setStaffRoleFilter] = useState('ALL');
  
  const [scanMode, setScanMode] = useState<'NONE' | 'IN' | 'OUT'>('NONE');
  const [scanInput, setScanInput] = useState('');
  const [lastScanned, setLastScanned] = useState<{name: string, time: string, type: 'IN'|'OUT'} | null>(null);
  const [autoEnter, setAutoEnter] = useState<boolean>(true);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const lastProcessedRef = useRef<{ code: string; time: number } | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initDataAndSubscribe = async () => {
      setLoading(true);
      try {
        const [surveys, questionsList, staffList] = await Promise.all([
          getSurveys(),
          getDynamicQuestions(),
          getStaff()
        ]);
        
        let allStudents: any[] = [];
        for (const survey of surveys) {
          allStudents = [...allStudents, ...extractStudentsFromSurvey(survey, questionsList)];
        }
        setStudents(allStudents);
        setQuestions(questionsList);
        setStaff(staffList);

        // Set up real-time subscription for attendance records
        unsubscribe = subscribeAttendanceByDate(date, (records) => {
          setAttendance(records);
          setLoading(false);
        });
      } catch (e) {
        console.error(e);
        addToast("Failed to load attendance data", "error");
        setLoading(false);
      }
    };

    initDataAndSubscribe();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [date]);

  const fetchData = async () => {
    try {
      const [surveys, questionsList, staffList] = await Promise.all([
        getSurveys(),
        getDynamicQuestions(),
        getStaff()
      ]);
      
      let allStudents: any[] = [];
      for (const survey of surveys) {
        allStudents = [...allStudents, ...extractStudentsFromSurvey(survey, questionsList)];
      }
      setStudents(allStudents);
      setQuestions(questionsList);
      setStaff(staffList);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (scanMode as any !== 'NONE') {
      scanInputRef.current?.focus();
    }
  }, [scanMode]);

  const matchSerialNo = (dbVal: any, inputVal: any): boolean => {
    if (dbVal === undefined || dbVal === null || inputVal === undefined || inputVal === null) return false;
    const db = String(dbVal).trim().toUpperCase();
    const inp = String(inputVal).trim().toUpperCase();
    
    // 1. Exact match after trimming/uppercasing
    if (db === inp) return true;
    
    // 2. Match after removing all non-alphanumeric characters
    const cleanDb = db.replace(/[^A-Z0-9]/g, '');
    const cleanInp = inp.replace(/[^A-Z0-9]/g, '');
    if (cleanDb === cleanInp) return true;

    // Helper to strip "DRAFT" and break into prefix (letters) and suffix (number)
    const parseCode = (str: string) => {
      const withoutDraft = str.replace(/DRAFT/g, '').replace(/[^A-Z0-9]/g, '');
      const match = withoutDraft.match(/^([A-Z]*)(0*[0-9]+)$/);
      if (match) {
        return { prefix: match[1], num: parseInt(match[2], 10) };
      }
      return null;
    };

    const parsedDb = parseCode(cleanDb) || parseCode(db);
    const parsedInp = parseCode(cleanInp) || parseCode(inp);

    if (parsedDb && parsedInp) {
      // If the input has no prefix (letters), allow matching based on numeric ID alone (highly convenient for manual entry)
      if (parsedInp.prefix === "") {
        return parsedDb.num === parsedInp.num;
      }
      // If the input has a prefix, require both prefixes to match exactly, and the numeric values to match
      return parsedDb.prefix === parsedInp.prefix && parsedDb.num === parsedInp.num;
    }

    // Fallback: simple inclusion check
    return cleanDb.includes(cleanInp) || cleanInp.includes(cleanDb);
  };

  const findStudentByInput = (inputVal: string) => {
    if (!inputVal.trim()) return null;
    const serialNo = inputVal.trim();

    return students.find(s => 
      matchSerialNo(s.serialNo, serialNo) || 
      matchSerialNo(s.id, serialNo) || 
      matchSerialNo(s.idNo, serialNo) || 
      matchSerialNo(s.id_no, serialNo) ||
      (s.studentName && s.studentName.trim().toUpperCase() === serialNo.toUpperCase())
    );
  };

  const findStaffByInput = (inputVal: string) => {
    if (!inputVal.trim()) return null;
    const serialNo = inputVal.trim();

    return staff.find(s => 
      matchSerialNo(s.serialNo, serialNo) || 
      matchSerialNo(s.id, serialNo) || 
      (s.name && s.name.trim().toUpperCase() === serialNo.toUpperCase())
    );
  };

  const processScanCode = async (code: string) => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    // Prevent duplicate triggers for the exact same scan/keystroke sequence within 1.5 seconds
    const now = Date.now();
    if (
      lastProcessedRef.current && 
      lastProcessedRef.current.code === trimmedCode && 
      (now - lastProcessedRef.current.time) < 1500
    ) {
      setScanInput(''); // Clear duplicate input
      return;
    }

    lastProcessedRef.current = { code: trimmedCode, time: now };
    setScanInput(''); // Clear immediately for next scans

    const student = findStudentByInput(trimmedCode);

    if (!student) {
      // Try scanning as staff
      const staffMember = findStaffByInput(trimmedCode);
      if (!staffMember) {
        addToast(`Unknown ID: ${trimmedCode}`, "error");
        return;
      }

      const timeString = new Date().toISOString();
      try {
        await markAttendance(date, staffMember.id, staffMember.name, scanMode as 'IN' | 'OUT', timeString, true, staffMember.role);
        
        // Update local state instantly for UI
        setAttendance(prev => {
          const existing = prev.find(a => a.studentId === staffMember.id);
          if (existing) {
            return prev.map(a => a.studentId === staffMember.id ? { 
              ...a, 
              inTime: scanMode === 'IN' ? timeString : a.inTime,
              outTime: scanMode === 'OUT' ? timeString : a.outTime,
              status: 'PRESENT'
            } : a);
          } else {
            return [...prev, {
              id: Date.now().toString(),
              date,
              studentId: staffMember.id,
              studentName: staffMember.name,
              inTime: scanMode === 'IN' ? timeString : undefined,
              outTime: scanMode === 'OUT' ? timeString : undefined,
              status: 'PRESENT',
              isStaff: true,
              staffRole: staffMember.role
            }];
          }
        });
        
        setLastScanned({
          name: `${staffMember.name} (Staff - ${staffMember.role === 'faculty' ? 'Faculty' : 'Coordinator'})`,
          time: timeString,
          type: scanMode as 'IN' | 'OUT'
        });
        
        addToast(`Staff Attendance recorded: ${staffMember.name} (${scanMode})`, "success");
      } catch (err) {
        addToast("Failed to record staff attendance", "error");
      }
      return;
    }

    const timeString = new Date().toISOString();
    try {
      await markAttendance(date, student.id, student.studentName, scanMode as 'IN' | 'OUT', timeString);
      
      // Update local state instantly for UI
      setAttendance(prev => {
        const existing = prev.find(a => a.studentId === student.id);
        if (existing) {
          return prev.map(a => a.studentId === student.id ? { 
            ...a, 
            inTime: scanMode === 'IN' ? timeString : a.inTime,
            outTime: scanMode === 'OUT' ? timeString : a.outTime,
            status: 'PRESENT'
          } : a);
        } else {
          return [...prev, {
            id: Date.now().toString(),
            date,
            studentId: student.id,
            studentName: student.studentName,
            inTime: scanMode === 'IN' ? timeString : undefined,
            outTime: scanMode === 'OUT' ? timeString : undefined,
            status: 'PRESENT'
          }];
        }
      });
      
      setLastScanned({
        name: student.studentName,
        time: timeString,
        type: scanMode as 'IN' | 'OUT'
      });
      
      addToast(`Attendance recorded: ${student.studentName} (${scanMode})`, "success");
    } catch (e) {
      addToast("Failed to record attendance", "error");
    }
  };

  const handleScanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await processScanCode(scanInput);
  };

  // Smart Auto Enter handling - pure debounced fallback check to prevent race conditions during typing
  useEffect(() => {
    if (scanMode === 'NONE' || !scanInput.trim() || !autoEnter) return;

    // Debounced fallback check: Wait for typing to pause for 600ms before auto-submitting.
    // This prevents partial sequential ID interceptions (e.g. typing "LS-0010" doesn't trigger "LS-0001" mid-way).
    const timer = setTimeout(() => {
      processScanCode(scanInput);
    }, 600);

    return () => clearTimeout(timer);
  }, [scanInput, scanMode, autoEnter]);

  const startScanning = (mode: 'IN' | 'OUT') => {
    setScanMode(mode);
    setLastScanned(null);
  };

  const stopScanning = () => {
    setScanMode('NONE');
    setScanInput('');
  };
  
  const handleManualMark = async (member: any, type: 'IN' | 'OUT', isStaffMember: boolean = false) => {
    const timeString = new Date().toISOString();
    try {
      if (isStaffMember) {
        await markAttendance(date, member.id, member.name, type, timeString, true, member.role);
      } else {
        await markAttendance(date, member.id, member.studentName, type, timeString);
      }
      fetchData(); // reload
      addToast(`Marked ${isStaffMember ? member.name : member.studentName} as ${type}`, "success");
    } catch (e) {
      addToast("Failed to mark attendance", "error");
    }
  };

  const presentCount = attendance.filter(a => a.inTime && !a.isStaff).length;
  const leftCount = attendance.filter(a => a.outTime && !a.isStaff).length;

  const getCategoryStats = (catCode: 'LS' | 'CC' | 'FL') => {
    const catStudents = students.filter(s => {
      const age = getMemberAge(s, questions);
      return getCategoryForAge(age) === catCode;
    });
    
    const total = catStudents.length;
    let present = 0;
    let inside = 0;
    let checkedOut = 0;
    
    catStudents.forEach(s => {
      const record = attendance.find(a => a.studentId === s.id);
      if (record?.inTime) {
        present++;
        if (!record.outTime) {
          inside++;
        } else {
          checkedOut++;
        }
      }
    });

    const absent = total - present;
    const presentRate = total > 0 ? Math.round((present / total) * 100) : 0;

    return { total, present, inside, checkedOut, absent, presentRate };
  };

  const lsStats = getCategoryStats('LS');
  const ccStats = getCategoryStats('CC');
  const flStats = getCategoryStats('FL');

  const getStaffRoleStats = (role: 'faculty' | 'coordinator') => {
    const roleStaff = staff.filter(s => s.role === role);
    const total = roleStaff.length;
    let present = 0;
    let inside = 0;
    let checkedOut = 0;

    roleStaff.forEach(s => {
      const record = attendance.find(a => a.studentId === s.id);
      if (record?.inTime) {
        present++;
        if (!record.outTime) {
          inside++;
        } else {
          checkedOut++;
        }
      }
    });

    const absent = total - present;
    const presentRate = total > 0 ? Math.round((present / total) * 100) : 0;

    return { total, present, inside, checkedOut, absent, presentRate };
  };

  const facultyStats = getStaffRoleStats('faculty');
  const coordinatorStats = getStaffRoleStats('coordinator');

  // Filter students
  const filteredStudents = students.filter(student => {
    const record = attendance.find(a => a.studentId === student.id);
    const hasIn = !!record?.inTime;
    const hasOut = !!record?.outTime;
    
    // 1. Search Query Filter (Name or Serial Number)
    const q = searchQuery.toLowerCase().trim();
    const nameMatch = student.studentName?.toLowerCase().includes(q);
    const serialMatch = student.serialNo?.toLowerCase().includes(q) || student.studentIdNo?.toLowerCase().includes(q);
    const matchesSearch = !q || nameMatch || serialMatch;

    // 2. Category Filter
    const age = getMemberAge(student, questions);
    const category = getCategoryForAge(age);
    const matchesCategory = categoryFilter === 'ALL' || category === categoryFilter;

    // 3. Status Filter
    let matchesStatus = true;
    if (statusFilter === 'PRESENT') {
      matchesStatus = hasIn;
    } else if (statusFilter === 'ABSENT') {
      matchesStatus = !hasIn;
    } else if (statusFilter === 'IN_NOT_OUT') {
      matchesStatus = hasIn && !hasOut;
    } else if (statusFilter === 'OUT') {
      matchesStatus = hasOut;
    }

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const downloadCSVReport = () => {
    const headers = ['Serial No', 'Student Name', 'Category', 'Status', 'In Time', 'Out Time', 'Parent Name', 'Phone'];
    
    // Filter out staff records to get only students
    const rows = students.map(student => {
      const record = attendance.find(a => a.studentId === student.id);
      const age = getMemberAge(student, questions);
      const cat = getCategoryForAge(age);
      const catName = getCategoryName(cat) || 'N/A';
      
      let status = 'ABSENT';
      if (record?.inTime) {
        status = record.outTime ? 'CHECKED OUT' : 'STILL INSIDE';
      }

      const inTimeStr = record?.inTime ? new Date(record.inTime).toLocaleTimeString() : '-';
      const outTimeStr = record?.outTime ? new Date(record.outTime).toLocaleTimeString() : '-';
      
      return [
        student.serialNo || '',
        student.studentName || '',
        catName,
        status,
        inTimeStr,
        outTimeStr,
        student.parentName || '',
        student.phone || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Student_Attendance_Report_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDFReport = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Header Background
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('ATTENDANCE REPORT', 15, 18);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text(`ATTENDANCE REPORT - ${new Date(date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, 15, 28);

    // Summary Statistics
    doc.setTextColor(30, 41, 59); // slate-800
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Attendance Summary', 15, 50);

    // Draw Stats Table/Box
    doc.setFillColor(248, 250, 252); // slate-50
    doc.rect(15, 55, 180, 24, 'F');
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.rect(15, 55, 180, 24);

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.setFont('helvetica', 'normal');
    doc.text('Total Students', 25, 62);
    doc.text('Present Count', 65, 62);
    doc.text('Still Inside', 110, 62);
    doc.text('Checked Out', 155, 62);

    doc.setTextColor(15, 23, 42); // slate-900
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(String(students.length), 25, 72);
    doc.text(String(presentCount), 65, 72);
    doc.text(String(presentCount - leftCount), 110, 72);
    doc.text(String(leftCount), 155, 72);

    // Category breakdown
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59); // slate-800
    doc.text('Category Breakdown', 15, 90);

    const categories = [
      { name: 'Little Stars (LS)', stats: lsStats },
      { name: 'Creative Champs (CC)', stats: ccStats },
      { name: 'Future Leaders (FL)', stats: flStats }
    ];

    let startY = 96;
    categories.forEach((cat, index) => {
      doc.setFillColor(255, 255, 255);
      doc.rect(15, startY + (index * 12), 180, 10);
      doc.setDrawColor(241, 245, 249); // slate-100
      doc.rect(15, startY + (index * 12), 180, 10);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(cat.name, 20, startY + (index * 12) + 6.5);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Total: ${cat.stats.total}`, 70, startY + (index * 12) + 6.5);
      doc.text(`Present: ${cat.stats.present}`, 105, startY + (index * 12) + 6.5);
      doc.text(`Still Inside: ${cat.stats.inside}`, 140, startY + (index * 12) + 6.5);
      doc.text(`Rate: ${cat.stats.presentRate}%`, 172, startY + (index * 12) + 6.5);
    });

    // Detailed Student List
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59); // slate-800
    doc.text('Detailed Student Attendance', 15, 142);

    // Draw table headers
    doc.setFillColor(241, 245, 249); // slate-100
    doc.rect(15, 147, 180, 8, 'F');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105); // slate-600
    doc.text('ID No', 18, 152.5);
    doc.text('Student Name', 45, 152.5);
    doc.text('Category', 95, 152.5);
    doc.text('Status', 135, 152.5);
    doc.text('In Time', 160, 152.5);
    doc.text('Out Time', 180, 152.5);

    let rowY = 161;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42); // slate-900

    students.forEach((student, sIdx) => {
      if (rowY > 280) {
        doc.addPage();
        doc.setFillColor(241, 245, 249); // slate-100
        doc.rect(15, 15, 180, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text('ID No', 18, 20.5);
        doc.text('Student Name', 45, 20.5);
        doc.text('Category', 95, 20.5);
        doc.text('Status', 135, 20.5);
        doc.text('In Time', 160, 20.5);
        doc.text('Out Time', 180, 20.5);
        rowY = 28;
      }

      const record = attendance.find(a => a.studentId === student.id);
      const age = getMemberAge(student, questions);
      const cat = getCategoryForAge(age);
      const catName = getCategoryName(cat) || 'N/A';

      let status = 'ABSENT';
      if (record?.inTime) {
        status = record.outTime ? 'CHECKED OUT' : 'STILL INSIDE';
      }

      const inTimeStr = record?.inTime ? new Date(record.inTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
      const outTimeStr = record?.outTime ? new Date(record.outTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      
      if (sIdx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(15, rowY - 4, 180, 6, 'F');
      }

      doc.text(student.serialNo || 'PND', 18, rowY);
      doc.text(student.studentName || '', 45, rowY);
      doc.text(catName, 95, rowY);

      if (status === 'STILL INSIDE') {
        doc.setTextColor(16, 185, 129); // green
      } else if (status === 'CHECKED OUT') {
        doc.setTextColor(147, 51, 234); // purple
      } else {
        doc.setTextColor(148, 163, 184); // slate-400
      }
      doc.setFont('helvetica', 'bold');
      doc.text(status, 135, rowY);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      doc.text(inTimeStr, 160, rowY);
      doc.text(outTimeStr, 180, rowY);

      rowY += 6;
    });

    doc.save(`Student_Attendance_Report_${date}.pdf`);
  };

  const downloadStaffCSVReport = () => {
    const headers = ['Serial No', 'Staff Name', 'Role', 'Status', 'In Time', 'Out Time', 'Phone'];
    
    const rows = staff.map(member => {
      const record = attendance.find(a => a.studentId === member.id);
      const roleName = member.role === 'faculty' ? 'Faculty' : 'Coordinator';
      
      let status = 'ABSENT';
      if (record?.inTime) {
        status = record.outTime ? 'CHECKED OUT' : 'STILL INSIDE';
      }

      const inTimeStr = record?.inTime ? new Date(record.inTime).toLocaleTimeString() : '-';
      const outTimeStr = record?.outTime ? new Date(record.outTime).toLocaleTimeString() : '-';
      
      return [
        member.serialNo || '',
        member.name || '',
        roleName,
        status,
        inTimeStr,
        outTimeStr,
        member.phone || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Staff_Attendance_Report_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadStaffPDFReport = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Header Background
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('STAFF ATTENDANCE REPORT', 15, 18);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text(`STAFF ATTENDANCE REPORT - ${new Date(date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, 15, 28);

    // Summary Statistics
    doc.setTextColor(30, 41, 59); // slate-800
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Staff Attendance Summary', 15, 50);

    const staffPresent = staff.filter(m => attendance.some(a => a.studentId === m.id && a.inTime)).length;
    const staffLeft = staff.filter(m => attendance.some(a => a.studentId === m.id && a.outTime)).length;

    // Draw Stats Table/Box
    doc.setFillColor(248, 250, 252); // slate-50
    doc.rect(15, 55, 180, 24, 'F');
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.rect(15, 55, 180, 24);

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.setFont('helvetica', 'normal');
    doc.text('Total Staff', 25, 62);
    doc.text('Present Count', 65, 62);
    doc.text('Still Inside', 110, 62);
    doc.text('Checked Out', 155, 62);

    doc.setTextColor(15, 23, 42); // slate-900
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(String(staff.length), 25, 72);
    doc.text(String(staffPresent), 65, 72);
    doc.text(String(staffPresent - staffLeft), 110, 72);
    doc.text(String(staffLeft), 155, 72);

    // Role breakdown
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59); // slate-800
    doc.text('Role Breakdown', 15, 90);

    const roles = [
      { name: 'Faculty', key: 'faculty' },
      { name: 'Coordinator', key: 'coordinator' }
    ];

    let startY = 96;
    roles.forEach((r, index) => {
      const totalRole = staff.filter(s => s.role === r.key).length;
      const presentRole = staff.filter(s => s.role === r.key && attendance.some(a => a.studentId === s.id && a.inTime)).length;
      const insideRole = staff.filter(s => s.role === r.key && attendance.some(a => a.studentId === s.id && a.inTime && !a.outTime)).length;
      const rate = totalRole > 0 ? Math.round((presentRole / totalRole) * 100) : 0;

      doc.setFillColor(255, 255, 255);
      doc.rect(15, startY + (index * 12), 180, 10);
      doc.setDrawColor(241, 245, 249); // slate-100
      doc.rect(15, startY + (index * 12), 180, 10);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(r.name, 20, startY + (index * 12) + 6.5);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Total: ${totalRole}`, 70, startY + (index * 12) + 6.5);
      doc.text(`Present: ${presentRole}`, 105, startY + (index * 12) + 6.5);
      doc.text(`Still Inside: ${insideRole}`, 140, startY + (index * 12) + 6.5);
      doc.text(`Rate: ${rate}%`, 172, startY + (index * 12) + 6.5);
    });

    // Detailed Staff List
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59); // slate-800
    doc.text('Detailed Staff Attendance', 15, 130);

    // Draw table headers
    doc.setFillColor(241, 245, 249); // slate-100
    doc.rect(15, 135, 180, 8, 'F');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105); // slate-600
    doc.text('Serial No', 18, 140.5);
    doc.text('Staff Name', 45, 140.5);
    doc.text('Role', 95, 140.5);
    doc.text('Status', 135, 140.5);
    doc.text('In Time', 160, 140.5);
    doc.text('Out Time', 180, 140.5);

    let rowY = 149;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42); // slate-900

    staff.forEach((member, sIdx) => {
      if (rowY > 280) {
        doc.addPage();
        doc.setFillColor(241, 245, 249); // slate-100
        doc.rect(15, 15, 180, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.text('Serial No', 18, 20.5);
        doc.text('Staff Name', 45, 20.5);
        doc.text('Role', 95, 20.5);
        doc.text('Status', 135, 20.5);
        doc.text('In Time', 160, 20.5);
        doc.text('Out Time', 180, 20.5);
        rowY = 28;
      }

      const record = attendance.find(a => a.studentId === member.id);
      const roleName = member.role === 'faculty' ? 'Faculty' : 'Coordinator';

      let status = 'ABSENT';
      if (record?.inTime) {
        status = record.outTime ? 'CHECKED OUT' : 'STILL INSIDE';
      }

      const inTimeStr = record?.inTime ? new Date(record.inTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
      const outTimeStr = record?.outTime ? new Date(record.outTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      
      if (sIdx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(15, rowY - 4, 180, 6, 'F');
      }

      doc.text(member.serialNo || 'PND', 18, rowY);
      doc.text(member.name || '', 45, rowY);
      doc.text(roleName, 95, rowY);

      if (status === 'STILL INSIDE') {
        doc.setTextColor(16, 185, 129); // green
      } else if (status === 'CHECKED OUT') {
        doc.setTextColor(147, 51, 234); // purple
      } else {
        doc.setTextColor(148, 163, 184); // slate-400
      }
      doc.setFont('helvetica', 'bold');
      doc.text(status, 135, rowY);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      doc.text(inTimeStr, 160, rowY);
      doc.text(outTimeStr, 180, rowY);

      rowY += 6;
    });

    doc.save(`Staff_Attendance_Report_${date}.pdf`);
  };

  if (loading && students.length === 0) {
    return <div className="p-8 text-center"><Loader className="animate-spin mx-auto text-emerald-600" size={32} /></div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4 border-b gap-4">
        <div>
          <h2 className="text-xl font-black text-gray-800 uppercase flex items-center">
            <ScanBarcode className="mr-2 text-emerald-600" size={24}/>
            Attendance Management
          </h2>
          <p className="text-sm text-gray-500 font-medium mt-1">Scan student ID cards or manually mark attendance.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            onClick={() => setShowReport(!showReport)}
            className={`px-4 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition border ${
              showReport 
                ? 'bg-amber-500 text-white border-amber-600 hover:bg-amber-600' 
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 shadow-sm'
            }`}
          >
            <FileText size={16} />
            {showReport ? 'Hide End of Day Report' : 'End of Day Report'}
          </button>

          <div className="flex items-center space-x-3 bg-gray-50 p-2 rounded-lg border">
            <Calendar className="text-gray-400" size={20} />
            <input 
              type="date" 
              value={date} 
              onChange={e => setDate(e.target.value)}
              className="bg-transparent border-none outline-none text-gray-700 font-bold text-sm"
            />
          </div>
        </div>
      </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-blue-600 text-sm font-bold uppercase">Total Students</p>
            <p className="text-3xl font-black text-blue-900 mt-1">{students.length}</p>
          </div>
          <Users size={32} className="text-blue-300" />
        </div>
        <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-emerald-600 text-sm font-bold uppercase">Present Today (In)</p>
            <p className="text-3xl font-black text-emerald-900 mt-1">{presentCount}</p>
          </div>
          <CheckCircle size={32} className="text-emerald-300" />
        </div>
        <div className="bg-purple-50 border border-purple-100 p-4 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-purple-600 text-sm font-bold uppercase">Left Today (Out)</p>
            <p className="text-3xl font-black text-purple-900 mt-1">{leftCount}</p>
          </div>
          <Clock size={32} className="text-purple-300" />
        </div>
      </div>

      {showReport && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8 shadow-sm animate-fade-in">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4 border-b border-slate-200 gap-4">
            <div>
              <h3 className="text-lg font-black text-slate-800 uppercase flex items-center gap-2">
                <Sparkles className="text-amber-500" size={20} />
                Daily Attendance Report Summary
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-1">Generated overview for {new Date(date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>

            <div className="flex gap-2 w-full md:w-auto">
              {reportTab === 'STUDENT' ? (
                <>
                  <button
                    onClick={downloadPDFReport}
                    className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm"
                  >
                    <Download size={14} /> Download Student PDF
                  </button>
                  <button
                    onClick={downloadCSVReport}
                    className="flex-1 md:flex-none bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm"
                  >
                    <Download size={14} /> Download Student CSV
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={downloadStaffPDFReport}
                    className="flex-1 md:flex-none bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm"
                  >
                    <Download size={14} /> Download Staff PDF
                  </button>
                  <button
                    onClick={downloadStaffCSVReport}
                    className="flex-1 md:flex-none bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm"
                  >
                    <Download size={14} /> Download Staff CSV
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Report Type Tabs */}
          <div className="flex border-b border-slate-200 mb-6 gap-2">
            <button
              onClick={() => setReportTab('STUDENT')}
              className={`px-4 py-2 text-xs font-bold border-b-2 transition uppercase tracking-wide ${
                reportTab === 'STUDENT'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Student Report
            </button>
            <button
              onClick={() => setReportTab('STAFF')}
              className={`px-4 py-2 text-xs font-bold border-b-2 transition uppercase tracking-wide ${
                reportTab === 'STAFF'
                  ? 'border-slate-800 text-slate-800'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              Staff Report
            </button>
          </div>

          {reportTab === 'STUDENT' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Summary Statistics Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs animate-fade-in">
                <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-4 border-b pb-2">Overall Statistics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Registered Students:</span>
                    <span className="font-bold text-slate-800">{students.length}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Present Today:</span>
                    <span className="font-bold text-emerald-600">{presentCount}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Absent Today:</span>
                    <span className="font-bold text-red-500">{students.length - presentCount}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Still Inside (In, not Out):</span>
                    <span className="font-bold text-amber-500">{presentCount - leftCount}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Checked Out (Out):</span>
                    <span className="font-bold text-purple-600">{leftCount}</span>
                  </div>
                  <div className="pt-2 border-t flex justify-between items-center">
                    <span className="text-xs font-black text-slate-700 uppercase">Overall Present Rate:</span>
                    <span className="text-base font-black text-slate-800">
                      {students.length > 0 ? Math.round((presentCount / students.length) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Category Analysis Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs lg:col-span-2 animate-fade-in">
                <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-4 border-b pb-2">Category Performance Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="text-slate-400 uppercase tracking-wider border-b">
                        <th className="pb-2 font-bold">Category</th>
                        <th className="pb-2 font-bold text-center">Registered</th>
                        <th className="pb-2 font-bold text-center">Present</th>
                        <th className="pb-2 font-bold text-center">Absent</th>
                        <th className="pb-2 font-bold text-center">Still Inside</th>
                        <th className="pb-2 font-bold text-right">Attendance Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="py-2.5 font-bold text-slate-700">Little Stars (LS)</td>
                        <td className="py-2.5 text-center text-slate-600 font-medium">{lsStats.total}</td>
                        <td className="py-2.5 text-center font-bold text-emerald-600">{lsStats.present}</td>
                        <td className="py-2.5 text-center text-red-500 font-medium">{lsStats.absent}</td>
                        <td className="py-2.5 text-center text-amber-500 font-medium">{lsStats.inside}</td>
                        <td className="py-2.5 text-right font-black text-slate-800">{lsStats.presentRate}%</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 font-bold text-slate-700">Creative Champs (CC)</td>
                        <td className="py-2.5 text-center text-slate-600 font-medium">{ccStats.total}</td>
                        <td className="py-2.5 text-center font-bold text-emerald-600">{ccStats.present}</td>
                        <td className="py-2.5 text-center text-red-500 font-medium">{ccStats.absent}</td>
                        <td className="py-2.5 text-center text-amber-500 font-medium">{ccStats.inside}</td>
                        <td className="py-2.5 text-right font-black text-slate-800">{ccStats.presentRate}%</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 font-bold text-slate-700">Future Leaders (FL)</td>
                        <td className="py-2.5 text-center text-slate-600 font-medium">{flStats.total}</td>
                        <td className="py-2.5 text-center font-bold text-emerald-600">{flStats.present}</td>
                        <td className="py-2.5 text-center text-red-500 font-medium">{flStats.absent}</td>
                        <td className="py-2.5 text-center text-amber-500 font-medium">{flStats.inside}</td>
                        <td className="py-2.5 text-right font-black text-slate-800">{flStats.presentRate}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 lg:col-span-3">
                <AlertTriangle size={18} className="text-amber-500 shrink-0" />
                <div>
                  <span className="font-bold">Did you know?</span> Still Inside count lists students who have been scanned/marked as <span className="font-bold">IN</span> but have not yet checked out (<span className="font-bold">OUT</span>) for today. Use the filters below to isolate this group for parent pickup coordination.
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Summary Statistics Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs animate-fade-in">
                <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-4 border-b pb-2">Staff Statistics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Registered Staff:</span>
                    <span className="font-bold text-slate-800">{staff.length}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Present Today:</span>
                    <span className="font-bold text-emerald-600">
                      {staff.filter(m => attendance.some(a => a.studentId === m.id && a.inTime)).length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Absent Today:</span>
                    <span className="font-bold text-red-500">
                      {staff.length - staff.filter(m => attendance.some(a => a.studentId === m.id && a.inTime)).length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Still Inside:</span>
                    <span className="font-bold text-amber-500">
                      {staff.filter(m => attendance.some(a => a.studentId === m.id && a.inTime && !a.outTime)).length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Checked Out:</span>
                    <span className="font-bold text-purple-600">
                      {staff.filter(m => attendance.some(a => a.studentId === m.id && a.outTime)).length}
                    </span>
                  </div>
                  <div className="pt-2 border-t flex justify-between items-center">
                    <span className="text-xs font-black text-slate-700 uppercase">Staff Present Rate:</span>
                    <span className="text-base font-black text-slate-800">
                      {staff.length > 0 
                        ? Math.round((staff.filter(m => attendance.some(a => a.studentId === m.id && a.inTime)).length / staff.length) * 100) 
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Role Analysis Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs lg:col-span-2 animate-fade-in">
                <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider mb-4 border-b pb-2">Role Performance Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="text-slate-400 uppercase tracking-wider border-b">
                        <th className="pb-2 font-bold">Role</th>
                        <th className="pb-2 font-bold text-center">Registered</th>
                        <th className="pb-2 font-bold text-center">Present</th>
                        <th className="pb-2 font-bold text-center">Absent</th>
                        <th className="pb-2 font-bold text-center">Still Inside</th>
                        <th className="pb-2 font-bold text-right">Attendance Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="py-2.5 font-bold text-slate-700">Faculty</td>
                        <td className="py-2.5 text-center text-slate-600 font-medium">{facultyStats.total}</td>
                        <td className="py-2.5 text-center font-bold text-emerald-600">{facultyStats.present}</td>
                        <td className="py-2.5 text-center text-red-500 font-medium">{facultyStats.absent}</td>
                        <td className="py-2.5 text-center text-amber-500 font-medium">{facultyStats.inside}</td>
                        <td className="py-2.5 text-right font-black text-slate-800">{facultyStats.presentRate}%</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 font-bold text-slate-700">Coordinator</td>
                        <td className="py-2.5 text-center text-slate-600 font-medium">{coordinatorStats.total}</td>
                        <td className="py-2.5 text-center font-bold text-emerald-600">{coordinatorStats.present}</td>
                        <td className="py-2.5 text-center text-red-500 font-medium">{coordinatorStats.absent}</td>
                        <td className="py-2.5 text-center text-amber-500 font-medium">{coordinatorStats.inside}</td>
                        <td className="py-2.5 text-right font-black text-slate-800">{coordinatorStats.presentRate}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-800 lg:col-span-3 animate-fade-in">
                <Users size={18} className="text-slate-500 shrink-0" />
                <div>
                  <span className="font-bold">Staff Attendance:</span> This report displays information specifically compiled for staff members (Faculty and Coordinators). You can download distinct reports matching this staff view using the buttons above.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!isViewOnly && (
        <>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <button 
              onClick={() => startScanning('IN')}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl font-bold text-lg shadow-sm transition-all active:scale-95 flex items-center justify-center"
            >
              <ScanBarcode className="mr-2" /> Start IN Scanning
            </button>
            <button 
              onClick={() => startScanning('OUT')}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-xl font-bold text-lg shadow-sm transition-all active:scale-95 flex items-center justify-center"
            >
              <ScanBarcode className="mr-2" /> Start OUT Scanning
            </button>
          </div>
          
          <div className="flex justify-end space-x-2 mb-8">
             <button 
                onClick={async () => {
                  if (window.confirm("Are you sure you want to bulk mark all students as IN?")) {
                    const timeString = new Date().toISOString();
                    for (const student of students) {
                      const record = attendance.find(a => a.studentId === student.id);
                      if (!record || !record.inTime) {
                        await markAttendance(date, student.id, student.studentName, 'IN', timeString);
                      }
                    }
                    fetchData();
                    addToast("Bulk marked all students as IN", "success");
                  }
                }}
                className="text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-4 py-2 rounded-lg font-bold text-sm"
             >
               Bulk Mark All IN
             </button>
             <button 
                onClick={async () => {
                  if (window.confirm("Are you sure you want to bulk mark all students as OUT?")) {
                    const timeString = new Date().toISOString();
                    for (const student of students) {
                      const record = attendance.find(a => a.studentId === student.id);
                      if (!record || !record.outTime) {
                        await markAttendance(date, student.id, student.studentName, 'OUT', timeString);
                      }
                    }
                    fetchData();
                    addToast("Bulk marked all students as OUT", "success");
                  }
                }}
                className="text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-4 py-2 rounded-lg font-bold text-sm"
             >
               Bulk Mark All OUT
             </button>
          </div>
        </>
      )}

      {/* Search & Filter Panel */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 animate-fade-in">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search Input */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search student by name or serial..."
              className="w-full bg-white border border-slate-300 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 font-bold"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 w-full md:w-auto items-center">
            {/* Category Dropdown */}
            <div className="flex items-center bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-700 shadow-sm">
              <Filter className="text-slate-400 mr-2" size={14} />
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="bg-transparent border-none outline-none cursor-pointer pr-4 font-bold text-xs"
              >
                <option value="ALL">All Categories</option>
                <option value="LS">Little Stars (LS)</option>
                <option value="CC">Creative Champs (CC)</option>
                <option value="FL">Future Leaders (FL)</option>
              </select>
            </div>

            {/* Attendance Status Dropdown */}
            <div className="flex items-center bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-700 shadow-sm">
              <Clock className="text-slate-400 mr-2" size={14} />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="bg-transparent border-none outline-none cursor-pointer pr-4 font-bold text-xs"
              >
                <option value="ALL">All Statuses</option>
                <option value="PRESENT">Present (Checked In)</option>
                <option value="ABSENT">Absent</option>
                <option value="IN_NOT_OUT">In, didn't Go Out</option>
                <option value="OUT">Checked Out</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b font-bold text-gray-700 uppercase text-sm flex justify-between items-center">
          <span>Attendance Record - {new Date(date).toLocaleDateString()}</span>
          <span className="text-xs text-gray-500 bg-gray-200/60 px-2 py-0.5 rounded-full font-bold">
            Showing {filteredStudents.length} of {students.length} students
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 border-b">Student Name</th>
                <th className="px-4 py-3 border-b">Serial No</th>
                <th className="px-4 py-3 border-b">Category</th>
                <th className="px-4 py-3 border-b">In Time</th>
                <th className="px-4 py-3 border-b">Out Time</th>
                {!isViewOnly && <th className="px-4 py-3 border-b text-right">Manual Mark</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan={isViewOnly ? 5 : 6} className="px-4 py-12 text-center text-gray-400 italic font-bold bg-white">
                    No students match the current search or filters.
                  </td>
                </tr>
              ) : filteredStudents.map(student => {
                const record = attendance.find(a => a.studentId === student.id);
                const age = getMemberAge(student, questions);
                const cat = getCategoryForAge(age);
                const catName = getCategoryName(cat) || 'N/A';
                return (
                  <tr key={student.id} className="hover:bg-gray-50 bg-white">
                    <td className="px-4 py-3 font-bold text-gray-800">
                      <div>{student.studentName}</div>
                      {student.parentName && (
                        <div className="text-[10px] text-gray-400 font-medium mt-0.5 normal-case">S/o: {student.parentName}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{student.serialNo}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                        cat === 'LS' ? 'bg-amber-100 text-amber-800' :
                        cat === 'CC' ? 'bg-indigo-100 text-indigo-800' :
                        cat === 'FL' ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-800'
                      }`}>
                        {catName}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {record?.inTime ? (
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs font-bold">
                          {new Date(record.inTime).toLocaleTimeString()}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {record?.outTime ? (
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-bold">
                          {new Date(record.outTime).toLocaleTimeString()}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">-</span>
                      )}
                    </td>
                    {!isViewOnly && (
                      <td className="px-4 py-3 text-right space-x-2">
                         {!record?.inTime && (
                           <button onClick={() => handleManualMark(student, 'IN')} className="text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded text-xs font-bold border border-emerald-200">
                             Mark IN
                           </button>
                         )}
                         {!record?.outTime && (
                           <button onClick={() => handleManualMark(student, 'OUT')} className="text-purple-600 hover:bg-purple-50 px-2 py-1 rounded text-xs font-bold border border-purple-200">
                             Mark OUT
                           </button>
                         )}
                         {record && (
                           <button 
                             onClick={async () => {
                               if (window.confirm(`Are you sure you want to clear the attendance for ${student.studentName}?`)) {
                                 await clearAttendance(date, student.id);
                                 fetchData();
                                 addToast(`Attendance cleared for ${student.studentName}`, "success");
                               }
                             }} 
                             className="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-xs font-bold border border-red-200 ml-2"
                           >
                             Clear
                           </button>
                         )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staff Attendance Record Table */}
      <div className="border rounded-lg overflow-hidden mt-8 animate-fade-in bg-white">
        <div className="bg-slate-800 text-white px-4 py-3 font-bold uppercase text-sm flex justify-between items-center">
          <span>Staff Attendance Record - {new Date(date).toLocaleDateString()}</span>
          <span className="text-xs text-slate-300 bg-slate-700/60 px-2 py-0.5 rounded-full font-bold">
            Showing {staff.length} staff members
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 border-b">Staff Name</th>
                <th className="px-4 py-3 border-b">Serial No</th>
                <th className="px-4 py-3 border-b">Role</th>
                <th className="px-4 py-3 border-b">In Time</th>
                <th className="px-4 py-3 border-b">Out Time</th>
                {!isViewOnly && <th className="px-4 py-3 border-b text-right">Manual Mark</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {staff.length === 0 ? (
                <tr>
                  <td colSpan={isViewOnly ? 5 : 6} className="px-4 py-12 text-center text-gray-400 italic font-bold bg-white">
                    No staff members registered.
                  </td>
                </tr>
              ) : staff.map(member => {
                const record = attendance.find(a => a.studentId === member.id);
                const roleLabel = member.role === 'faculty' ? 'Faculty' : 'Coordinator';
                return (
                  <tr key={member.id} className="hover:bg-gray-50 bg-white">
                    <td className="px-4 py-3 font-bold text-gray-800">
                      {member.name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{member.serialNo}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                        member.role === 'faculty' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {roleLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {record?.inTime ? (
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs font-bold">
                          {new Date(record.inTime).toLocaleTimeString()}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {record?.outTime ? (
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-bold">
                          {new Date(record.outTime).toLocaleTimeString()}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs italic">-</span>
                      )}
                    </td>
                    {!isViewOnly && (
                      <td className="px-4 py-3 text-right space-x-2">
                         {!record?.inTime && (
                           <button onClick={() => handleManualMark(member, 'IN', true)} className="text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded text-xs font-bold border border-emerald-200">
                             Mark IN
                           </button>
                         )}
                         {!record?.outTime && (
                           <button onClick={() => handleManualMark(member, 'OUT', true)} className="text-purple-600 hover:bg-purple-50 px-2 py-1 rounded text-xs font-bold border border-purple-200">
                             Mark OUT
                           </button>
                         )}
                         {record && (
                           <button 
                             onClick={async () => {
                               if (window.confirm(`Are you sure you want to clear the attendance for ${member.name}?`)) {
                                 await clearAttendance(date, member.id);
                                 fetchData();
                                 addToast(`Attendance cleared for ${member.name}`, "success");
                               }
                             }} 
                             className="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-xs font-bold border border-red-200 ml-2"
                           >
                             Clear
                           </button>
                         )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scanning Modal */}
      {scanMode as any !== 'NONE' && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center p-4 backdrop-blur-sm">
          <button 
            onClick={stopScanning}
            className="absolute top-6 right-6 text-white hover:text-red-400 transition-colors"
          >
            <X size={48} />
          </button>
          
          <div className="bg-white p-8 rounded-2xl max-w-lg w-full text-center shadow-2xl relative overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-2 ${scanMode === 'IN' ? 'bg-emerald-500' : 'bg-purple-500'}`}></div>
            
            <ScanBarcode size={64} className={`mx-auto mb-4 ${scanMode === 'IN' ? 'text-emerald-500' : 'text-purple-500'} animate-pulse`} />
            
            <h2 className="text-3xl font-black text-gray-800 mb-2 uppercase">
              {scanMode === 'IN' ? 'IN Scanning Active' : 'OUT Scanning Active'}
            </h2>
            <p className="text-gray-500 font-medium mb-8">
              Please scan the barcode on the student ID card...
            </p>

            <form onSubmit={handleScanSubmit} className="space-y-4">
              <div className="relative">
                <input 
                  ref={scanInputRef}
                  type="text" 
                  value={scanInput}
                  onChange={e => setScanInput(e.target.value)}
                  placeholder="Scan card or type serial ID..."
                  onBlur={() => {
                    // Keep focus forcefully on the input so barcode scanner always works
                    if (scanMode as any !== 'NONE') {
                      setTimeout(() => scanInputRef.current?.focus(), 50);
                    }
                  }}
                  className={`w-full px-4 py-3.5 text-center font-mono text-xl font-black border-2 rounded-xl focus:outline-none focus:ring-4 transition-all uppercase tracking-wider ${scanMode === 'IN' ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-100 bg-emerald-50/50 text-emerald-900' : 'border-purple-300 focus:border-purple-500 focus:ring-purple-100 bg-purple-50/50 text-purple-900'}`}
                  autoFocus
                />
                
                {/* Visual indicator of focus state */}
                <div className="mt-3 flex items-center justify-center space-x-2 text-xs font-black tracking-wider text-gray-500 uppercase">
                  <span className={`w-2.5 h-2.5 rounded-full inline-block animate-pulse ${scanMode === 'IN' ? 'bg-emerald-500' : 'bg-purple-500'}`}></span>
                  <span>Scanner focus is active</span>
                </div>
              </div>
              
              {/* Auto Enter Toggle Component */}
              <div className="flex items-center justify-between bg-gray-50 p-3.5 rounded-xl border border-gray-200 shadow-sm transition-all">
                <div className="text-left pr-2">
                  <p className="text-xs font-black text-gray-800 uppercase tracking-wide">Auto-Submit Scan</p>
                  <p className="text-[10px] text-gray-500 font-medium">Submits instantly on matches, or 600ms after scanning/typing</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input 
                    type="checkbox" 
                    checked={autoEnter}
                    onChange={e => setAutoEnter(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 ${scanMode === 'IN' ? 'peer-focus:ring-emerald-300 peer-checked:bg-emerald-600' : 'peer-focus:ring-purple-300 peer-checked:bg-purple-600'} after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white`}></div>
                </label>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  className={`flex-1 text-white font-black py-2.5 px-4 rounded-xl shadow-md transition-all active:scale-95 text-sm uppercase tracking-wide ${scanMode === 'IN' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200' : 'bg-purple-600 hover:bg-purple-700 shadow-purple-200'}`}
                >
                  Submit Code
                </button>
                <button
                  type="button"
                  onClick={() => setScanInput('')}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2.5 px-4 rounded-xl border border-gray-200 transition-colors text-sm uppercase tracking-wide"
                >
                  Clear
                </button>
              </div>
            </form>

            {lastScanned && (
              <div className="mt-8 animate-in slide-in-from-bottom-4 fade-in duration-300">
                <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${lastScanned.type === 'IN' ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'}`}>
                  <CheckCircle size={16} className="mr-2" />
                  Successfully marked {lastScanned.type} for {lastScanned.name} at {new Date(lastScanned.time).toLocaleTimeString()}
                </div>
              </div>
            )}
          </div>
          
          <p className="text-white mt-8 text-sm opacity-70">
            Scanner focus is locked. You can safely scan multiple cards in a row.
          </p>
        </div>
      )}
    </div>
  );
};

export default AttendanceManager;
